/**
 * Branded primitive types for domain entity IDs.
 *
 * These types give each category of identifier a distinct compile-time type,
 * preventing accidental mixing of IDs (e.g. passing a PassengerId where a
 * TripId is expected). Use the companion factory functions (`toUserId`, etc.)
 * to create typed values at system boundaries where raw numbers or strings
 * arrive (API responses, navigation params, form values).
 */

/** Numeric ID for a v1 user record. */
export type UserId = number & { readonly _brand: 'UserId' };

/** Numeric ID for a passenger record. */
export type PassengerId = number & { readonly _brand: 'PassengerId' };

/** Numeric ID for a trip / booking record. */
export type TripId = number & { readonly _brand: 'TripId' };

/** Numeric ID for a passenger schedule record. */
export type ScheduleId = number & { readonly _brand: 'ScheduleId' };

/** Numeric ID for an agreement-partner record. */
export type AgreementPartnerId = number & { readonly _brand: 'AgreementPartnerId' };

/** String ID for a general absence record. */
export type AbsenceId = string & { readonly _brand: 'AbsenceId' };

/** String UUID for a v2 user record. */
export type UserUuid = string & { readonly _brand: 'UserUuid' };

// ─── Factory functions ────────────────────────────────────────────────────────
// Encapsulate the required type assertion so that callers never need `as`.

/** Converts a raw number to a {@link UserId} at the system boundary. */
export const toUserId = (id: number): UserId => id as UserId;

/** Converts a raw number to a {@link PassengerId} at the system boundary. */
export const toPassengerId = (id: number): PassengerId => id as PassengerId;

/** Converts a raw number to a {@link TripId} at the system boundary. */
export const toTripId = (id: number): TripId => id as TripId;

/** Converts a raw number to a {@link ScheduleId} at the system boundary. */
export const toScheduleId = (id: number): ScheduleId => id as ScheduleId;

/** Converts a raw number to an {@link AgreementPartnerId} at the system boundary. */
export const toAgreementPartnerId = (id: number): AgreementPartnerId => id as AgreementPartnerId;

/** Converts a raw string to an {@link AbsenceId} at the system boundary. */
export const toAbsenceId = (id: string): AbsenceId => id as AbsenceId;

/** Converts a raw string UUID to a {@link UserUuid} at the system boundary. */
export const toUserUuid = (uuid: string): UserUuid => uuid as UserUuid;

const PLACEHOLDER_USER_IDS: ReadonlySet<string> = new Set(['null', 'undefined', 'nan']);

const isPathSafeUserId = (value: string): boolean => /^[\w-]+$/.test(value);

export const parseUsableUserId = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;

  const normalized = value.trim();
  if (!normalized || PLACEHOLDER_USER_IDS.has(normalized.toLowerCase())) return undefined;

  return isPathSafeUserId(normalized) ? normalized : undefined;
};
